import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowRight, Download } from "lucide-react";
import { api, apiError, downloadPdf } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { MotionCard } from "@/components/motion";
import { LoadError } from "@/components/Primitives";

export default function Receipt() {
  const { t, money, lang } = useI18n();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const [tables, setTables] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReceipt = () => {
    setLoading(true);
    setLoadError(null);
    return Promise.all([
      api.get(`/orders/${orderId}`),
      api.get("/settings"),
      api.get("/tables"),
    ])
      .then(([a, b, c]) => {
        setOrder(a.data);
        setSettings(b.data || {});
        setTables(c.data.reduce((m, table) => ({ ...m, [table.id]: table }), {}));
      })
      .catch((e) => {
        setLoadError(e);
        toast.error(apiError(e));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReceipt();
  }, [orderId]);

  useEffect(() => {
    if (order) {
      const t1 = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t1);
    }
  }, [order]);

  const handleDownloadPdf = async () => {
    try {
      await downloadPdf(
        `/orders/${orderId}/receipt.pdf?lang=${lang}`,
        `receipt-${order?.order_number || orderId.slice(0, 8)}.pdf`,
      );
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (loading) return <div className="p-10 text-gray-500">{t("loading")}</div>;
  if (loadError) return <LoadError message={apiError(loadError)} onRetry={fetchReceipt} />;
  if (!order) return <LoadError message={t("empty")} onRetry={fetchReceipt} />;

  const width = settings.receipt_width === "58mm" ? "58mm" : "80mm";
  const dt = (order.paid_at || order.created_at).replace("T", " ").slice(0, 16);
  const table = order.table_id ? tables[order.table_id] : null;

  return (
    <div className="min-h-screen bg-gray-100" dir="rtl">
      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 p-3 flex items-center justify-between gap-2">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm">
          <ArrowRight className="w-4 h-4" /> {t("cancel")}
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="receipt-pdf" onClick={handleDownloadPdf} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-sm font-medium">
            <Download className="w-4 h-4" /> PDF
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#071A33] text-white text-sm font-medium">
            <Printer className="w-4 h-4" /> {t("printReceipt")}
          </button>
        </div>
      </div>

      <div className="py-8 flex justify-center">
        <MotionCard className="print-area bg-white p-4 text-black" style={{ width }}>
          <div className="text-center mb-3">
            <div className="font-bold text-base">{settings.restaurant_name || "رستورانت پلاس"}</div>
            <div className="text-xs">{settings.address || ""}</div>
            <div className="text-xs">{settings.phone || ""}</div>
            {settings.tax_number && <div className="text-xs">مالیات: {settings.tax_number}</div>}
          </div>
          <div className="border-t border-b border-dashed border-black/40 py-2 text-xs space-y-1">
            <div className="flex justify-between"><span>#</span><span className="font-mono">{order.order_number}</span></div>
            <div className="flex justify-between"><span>{t("date")}</span><span>{dt}</span></div>
            {order.table_id && <div className="flex justify-between"><span>{t("table")}</span><span>{table ? table.number : order.table_id}</span></div>}
            {order.waiter_name && <div className="flex justify-between"><span>{t("waiter")}</span><span>{order.waiter_name}</span></div>}
            {order.cashier_name && <div className="flex justify-between"><span>{t("cashierLabel")}</span><span>{order.cashier_name}</span></div>}
          </div>
          <table className="w-full text-xs my-2">
            <thead>
              <tr className="border-b border-dashed border-black/40">
                <th className="text-right py-1">{t("name")}</th>
                <th className="text-center py-1">{t("quantity")}</th>
                <th className="text-left py-1">{t("price")}</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it) => (
                <tr key={it.id}>
                  <td className="py-1">{lang === "pashto" ? it.name_snapshot_pashto : it.name_snapshot_dari}</td>
                  <td className="py-1 text-center tabular">{it.quantity}</td>
                  <td className="py-1 text-left tabular">{money(it.total_price).replace(" افغانی", "").replace(" افغانۍ", "")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-dashed border-black/40 pt-2 text-xs space-y-1">
            <div className="flex justify-between"><span>{t("subtotal")}</span><span className="tabular">{money(order.subtotal)}</span></div>
            {order.discount_amount > 0 && <div className="flex justify-between"><span>{t("discount")}</span><span className="tabular">-{money(order.discount_amount)}</span></div>}
            {order.tax_amount > 0 && <div className="flex justify-between"><span>{t("tax")}</span><span className="tabular">{money(order.tax_amount)}</span></div>}
            {order.service_charge_amount > 0 && <div className="flex justify-between"><span>{t("service")}</span><span className="tabular">{money(order.service_charge_amount)}</span></div>}
            <div className="flex justify-between text-sm font-bold border-t border-black/60 pt-1 mt-1">
              <span>{t("total")}</span><span className="tabular">{money(order.total || order.subtotal)}</span>
            </div>
            <div className="flex justify-between"><span>{t("paid")}</span><span className="tabular">{money(order.paid_amount)}</span></div>
            {order.debt_amount > 0 && <div className="flex justify-between text-rose-700 font-bold"><span>{t("debt")}</span><span className="tabular">{money(order.debt_amount)}</span></div>}
            {order.payment_method && <div className="flex justify-between"><span>{t("paymentMethod")}</span><span>{t(order.payment_method)}</span></div>}
          </div>
          <div className="text-center text-xs mt-3 border-t border-dashed border-black/40 pt-2">
            {settings.receipt_footer || (lang === "pashto" ? "ستاسو د راتګ نه مننه" : "از خرید شما سپاسگزاریم")}
          </div>
        </MotionCard>
      </div>
    </div>
  );
}
